import { useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import { useIssues } from "../../../controllers/useIssues";
import type { ProjectMember } from "../../../models/account";
import { ISSUE_STATUS_CATEGORY } from "../../../models/project";
import { IssuesView } from "../IssuesView";
import { BacklogView } from "../BacklogView";
import { CalendarView } from "../CalendarView";
import { GanttView } from "../GanttView";
import { EqmsSprintView } from "./EqmsSprintView";

type SprintTab = "overview" | "issues" | "backlog" | "calendar" | "gantt";
const TAB_LABEL: Record<SprintTab, string> = {
  overview: "개요", issues: "이슈", backlog: "백로그", calendar: "캘린더", gantt: "간트",
};

// eQMS 스프린트 섹션: 프로젝트 단위 스프린트/이슈 작업 공간. 상단 서브뷰 탭(개요·이슈·백로그·캘린더·간트)에서
// 기존 프로젝트 이슈 컴포넌트(IssuesView/BacklogView/CalendarView/GanttView)를 재사용한다. 오케스트레이션은
// useIssues 훅에 있고, 이 뷰는 탭 선택만 담당한다.
export function EqmsSprintBoardView({ eqms, members }: { eqms: EqmsController; members: ProjectMember[] }) {
  const issues = useIssues(eqms.projectId);
  const [tab, setTab] = useState<SprintTab>("overview");

  if (!eqms.projectId) return <div className="center">프로젝트를 선택하세요.</div>;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>스프린트</h2>
        <div className="note" style={{ marginTop: 4 }}>프로젝트 개발 주기 · 이슈 작업</div>
      </div>

      <div className="tabs">
        {(Object.keys(TAB_LABEL) as SprintTab[]).map((t) => (
          <button key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{TAB_LABEL[t]}</button>
        ))}
      </div>

      {tab === "overview" ? (
        <SprintOverview eqms={eqms} issues={issues} />
      ) : tab === "issues" ? (
        <IssuesView issues={issues} members={members} />
      ) : tab === "backlog" ? (
        <BacklogView issues={issues} />
      ) : tab === "calendar" ? (
        <CalendarView issues={issues} />
      ) : (
        <GanttView issues={issues} />
      )}
    </div>
  );
}

function SprintOverview({ eqms, issues }: { eqms: EqmsController; issues: ReturnType<typeof useIssues> }) {
  const todo = issues.issues.filter((i) => ISSUE_STATUS_CATEGORY[i.status] === "TODO").length;
  const inProgress = issues.issues.filter((i) => ISSUE_STATUS_CATEGORY[i.status] === "IN_PROGRESS").length;
  const done = issues.issues.filter((i) => ISSUE_STATUS_CATEGORY[i.status] === "DONE").length;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
        <SummaryTile label="전체 이슈" value={issues.issues.length} />
        <SummaryTile label="할 일" value={todo} />
        <SummaryTile label="진행 중" value={inProgress} />
        <SummaryTile label="완료" value={done} />
        <SummaryTile label="스프린트" value={issues.sprints.length} />
        <SummaryTile label="활성 스프린트" value={issues.activeSprint ? issues.activeSprint.name : "없음"} />
      </div>
      <EqmsSprintView eqms={eqms} />
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="info-card" style={{ padding: 14 }}>
      <div className="note" style={{ marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600 }}>{value}</div>
    </div>
  );
}
